import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import styles from "./VerifyEmailPage.module.scss";
import { api } from "../../services/api";
// import { ApiError } from "../../api/BaseAPI"; // adjust path if different

type Status = "idle" | "verifying" | "success" | "error";
type ResendStatus = "idle" | "sending" | "sent" | "error";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token")?.trim() || "", [params]);

  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string>("");

  // email can come from login redirect: navigate("/verify-email", { state: { email } })
  const stateEmail = (location.state as any)?.email as string | undefined;

  const [email, setEmail] = useState<string>(stateEmail ?? "");
  const [resendStatus, setResendStatus] = useState<ResendStatus>("idle");
  const [resendMsg, setResendMsg] = useState<string>("");
  const [cooldown, setCooldown] = useState<number>(0);

  // Token verification mode (email-click flow)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // If there's no token, we are in "login redirect" mode → no verification call here.
      if (!token) {
        setStatus("idle");
        setMessage("");
        return;
      }

      setStatus("verifying");
      setMessage("");

      try {
        const res = await api.app.getVerifyEmail({ token });

        if (cancelled) return;

        const data = (res as any)?.data ?? res;

        if (!data?.ok) {
          setStatus("error");
          setMessage(data?.message || "Invalid or expired verification link.");
          return;
        }

        setStatus("success");
        setMessage(data?.message || "Your email has been verified. You can log in now.");
      } catch (err: any) {
        if (cancelled) return;

        const apiMsg =
          err?.data?.message || // if using ApiError
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;

        setStatus("error");
        setMessage(apiMsg || "Verification failed.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Optional: redirect after success
  useEffect(() => {
    if (status !== "success") return;

    const timer = setTimeout(() => {
      navigate("/signin");
    }, 2500);

    return () => clearTimeout(timer);
  }, [status, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const onResend = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setResendStatus("error");
      setResendMsg("Please enter your email so we can resend the verification email.");
      return;
    }

    setResendStatus("sending");
    setResendMsg("");

    try {
      // call your resend endpoint here (adjust method name + payload)
      // Example: await api.resendVerificationEmail({ email: trimmed });
      await api.app.resendVerificationEmail({ email: trimmed });

      setResendStatus("sent");
      setResendMsg("If that email exists, we sent a new verification link/code.");
      setCooldown(60);
    } catch (err: any) {
      const apiMsg =
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to resend verification email.";

      setResendStatus("error");
      setResendMsg(apiMsg);
    }
  };

  // --- UI text
  const isTokenMode = !!token;

  const titleText =
    status === "success"
      ? "Email Verified"
      : status === "error"
      ? "Verification Failed"
      : "Verify Email";

  const statusLine =
    status === "verifying"
      ? "Verifying your email link…"
      : status === "success"
      ? message || "Your email has been verified. You can log in now."
      : status === "error"
      ? message || "We couldn’t verify this link."
      : isTokenMode
      ? "Waiting for verification token…"
      : "Check your email for a verification link or code.";

  return (
    <div className={styles.verifyEmailPage}>
      <div className={styles.card}>
        <div className={styles.cursive}>Hippocampus</div>
        <h1 className={styles.title}>{titleText}</h1>

        <div className={styles.statusBlock}>
          {status === "verifying" && <div className={styles.spinner} aria-label="Loading" />}

          <p className={styles.statusText}>
            {status === "success" && <span aria-hidden="true">✅&nbsp;</span>}
            {status === "error" && <span aria-hidden="true">❌&nbsp;</span>}
            {statusLine}
          </p>

          {/* If no token (login redirect flow) OR token failed, allow resend inline */}
          {!isTokenMode && (
            <div className={styles.resendBlock}>
              <label className={styles.label}>Email</label>
              <input
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />

              <button
                className={styles.buttonSecondary}
                onClick={onResend}
                disabled={resendStatus === "sending" || cooldown > 0}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : resendStatus === "sending" ? "Sending…" : "Resend verification"}
              </button>

              {resendMsg && <p className={styles.helperText}>{resendMsg}</p>}
            </div>
          )}

          {/* If token mode errored, also show resend (optional) */}
          {isTokenMode && status === "error" && (
            <div className={styles.resendBlock}>
              <button
                className={styles.buttonSecondary}
                onClick={onResend}
                disabled={resendStatus === "sending" || cooldown > 0 || !email.trim()}
                title={!email.trim() ? "Enter your email above or come from login so we know where to send it." : undefined}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : resendStatus === "sending" ? "Sending…" : "Resend verification"}
              </button>
              {resendMsg && <p className={styles.helperText}>{resendMsg}</p>}
            </div>
          )}

          <div className={styles.actions}>
            <Link className={styles.button} to="/signin">
              Go to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
